export type SmartSRPErrorCode =
    | 'SOMETHING_WENT_WRONG'
    | 'INPUT_TOO_LONG'
    | 'NETWORK_ERROR'
    | 'RATE_LIMITED'

interface SmartSortBannerMessages {
    heading: string
    description: string
    disclaimer?: string
}

interface SmartMetricsLabels {
    whyThisFlight: string
    scoreTooltip: string
    preferenceMatchTooltip: string
    dealLabels: {
        EXCELLENT: string
        GOOD: string
        FAIR: string
        EXPENSIVE: string
    }
}

interface SmartSRPMessages {
    smartSearchTitle: string
    smartSearchDescription: string
    smartSearchButton: string
    smartSearchButtonLoading: string
    smartSearchResetButton: string
    smartSearchPlaceHolderHints: string[]
    smartSortBanner: {
        default: SmartSortBannerMessages
        withPreferences: SmartSortBannerMessages
    }
    preferenceChips: {
        price: string
        duration: string
        stops: string
        departure: string
        arrival: string
        nonStop: string
        baggage: string
    }
    smartMetrics: SmartMetricsLabels
    errors: Record<SmartSRPErrorCode, string>
    tour: {
        searchBox: {
            title: string
            description: string
        }
        sortMessage: {
            title: string
            description: string
        }
        cardMetrics: {
            title: string
            description: string
        }
    },
    smartPreferencePartialMatch: string
    smartPreferenceOnlyPartialMatch: string,
    autoApplyPreferencesToggle: string
}
export const smartSRPMessages: Record<'en', SmartSRPMessages> & Record<'ar', SmartSRPMessages> = {


    en: {
        smartSearchTitle: 'Smart Search',
        smartSearchDescription: 'Tell us your preference and we\'ll filter and rank the best options for you.',
        smartSearchButton: 'Find best flights',
        smartSearchButtonLoading: 'Finding best flights...',
        smartSearchResetButton: 'Reset',
        smartSearchPlaceHolderHints: [
            '"Cheapest morning flight with baggage"',
            '"Non-stop flights only"',
            '"Arrive before 10 PM"',
            '"Early morning departure, under 5000"',
            '"Fastest flight, avoid long layovers"',
            '"Evening departure, land by morning"',
            '"Flights via Delhi or Mumbai"',
            '"Only IndiGo or Air India"',
            '"Flight with checked baggage"',
            '"Prefer afternoon departure, budget 8000"',
            '"Reach by 6 AM, cheapest option"',
            '"Avoid budget airlines, morning flight"',
            '"Short layover, arrive by evening"',
            '"Direct flight, depart after 6 PM"',
            '"Must land before midnight"',
            '"Flexible time, just the cheapest"',
            '"Morning arrival, prefer 1-stop max"',
        ],
        smartSortBanner: {
            default: {
                heading: 'Smart sorted for best value',
                description: 'Ranked by balancing price, duration and stops to surface the best overall options',
            },
            withPreferences: {
                heading: 'Sorted based on your preferences',
                description: 'Results are ranked to best match what you asked for',
                disclaimer: 'Some results may not match all preferences. The best result is chosen for overall relevance, balancing your preferences with price and availability.',
            },
        },
        preferenceChips: {
            price: 'Price',
            duration: 'Duration',
            stops: 'Stops',
            departure: 'Departure time',
            arrival: 'Arrival time',
            nonStop: 'Non-stop',
            baggage: 'Baggage',
        },
        smartMetrics: {
            whyThisFlight: 'Why this flight',
            scoreTooltip: 'Recommendation score out of 100',
            preferenceMatchTooltip: 'How well this flight matches your preferences',
            dealLabels: {
                EXCELLENT: 'Great deal',
                GOOD: 'Good deal',
                FAIR: 'Fair price',
                EXPENSIVE: 'Higher price',
            },
        },
        errors: {
            SOMETHING_WENT_WRONG: 'Something went wrong. Please try again.',
            INPUT_TOO_LONG: 'Your input is too long. Please shorten it and try again.',
            NETWORK_ERROR: 'Unable to connect. Please check your connection and try again.',
            RATE_LIMITED: 'Too many requests. Please wait a moment and try again.',
        },
        tour: {
            searchBox: {
                title: 'Meet Smart Search ✨',
                description: 'Just tell us what you want — like "cheapest morning flight with baggage and sort by price" — and we’ll find the best flights for you.',
            },
            sortMessage: {
                title: 'Results, sorted for you',
                description: 'Flights are ranked using your preferences — balancing price, duration, stops, and timing — so the best options appear first.',
            },
            cardMetrics: {
                title: 'Why this flight?',
                description: 'See why a flight is recommended — deal quality, price vs average, and duration vs average — all in one place.',
            },
        },
        smartPreferencePartialMatch: 'Here are some  options close to your preferences, even if the timing isn’t exact — have a look.',
        smartPreferenceOnlyPartialMatch: 'Flights are not fully  match your preferred time, but they meet some of your other preferences — have a look.',
        autoApplyPreferencesToggle: 'Auto apply preferences for updated results',
    },
    ar: {
        smartSearchTitle: 'البحث الذكي',
        smartSearchDescription: 'أخبرنا عن تفضيلاتك وسنفلتك ونرتب الخيارات الأفضل لك.',
        smartSearchButton: 'ابحث عن أفضل الطيران',
        smartSearchButtonLoading: 'جاري البحث عن أفضل الطيران...',
        smartSearchResetButton: 'إعادة ضبط',
        smartSearchPlaceHolderHints: [
            "أرخص رحلة صباحية مع شنطة",
            "رحلات مباشرة فقط",
            "الوصول قبل 10 مساءً",
            "مغادرة بدري الصبح، أقل من 5000",
            "أسرع رحلة، بدون انتظار طويل",
            "مغادرة بالليل، الوصول الصبح",
            "رحلات عن طريق دلهي أو مومباي",
            "فقط IndiGo أو Air India",
            "مع شنطة مشحونة",
            "يفضل مغادرة العصر، الميزانية 8000",
            "الوصول قبل 6 الصبح، أرخص خيار",
            "بدون طيران اقتصادي، رحلة صباحية",
            "توقف قصير، الوصول المساء",
            "رحلة مباشرة، مغادرة بعد 6 مساءً",
            "الوصول قبل منتصف الليل",
            "الوقت مرن، أهم شيء الأرخص",
            "الوصول صباحًا، بحد أقصى توقف واحد"
        ],
        smartSortBanner: {
            default: {
                heading: 'ترتيب ذكي لأفضل قيمة',
                description: 'مرتبة بتوازن بين السعر والمدة والمحطات لعرض أفضل الخيارات',
            },
            withPreferences: {
                heading: 'مرتبة بناءً على تفضيلاتك',
                description: 'النتائج مرتبة لتتوافق مع ما طلبته',
                disclaimer: 'بعض النتائج قد لا تتوافق مع جميع تفضيلاتك. يتم اختيار أفضل نتيجة بناءً على الملاءمة العامة، مع مراعاة تفضيلاتك والسعر والتوفر.',
            },
        },
        preferenceChips: {
            price: 'السعر',
            duration: 'المدة',
            stops: 'المحطات',
            departure: 'وقت المغادرة',
            arrival: 'وقت الوصول',
            nonStop: 'مباشر',
            baggage: 'الأمتعة',
        },
        smartMetrics: {
            whyThisFlight: 'لماذا هذه الرحلة',
            scoreTooltip: 'درجة التوصية من 100',
            preferenceMatchTooltip: 'مدى توافق هذه الرحلة مع تفضيلاتك',
            dealLabels: {
                EXCELLENT: 'صفقة ممتازة',
                GOOD: 'صفقة جيدة',
                FAIR: 'سعر مناسب',
                EXPENSIVE: 'سعر أعلى',
            },
        },
        errors: {
            SOMETHING_WENT_WRONG: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
            INPUT_TOO_LONG: 'النص طويل جدًا. يرجى تقصيره والمحاولة مرة أخرى.',
            NETWORK_ERROR: 'تعذر الاتصال. يرجى التحقق من اتصالك والمحاولة مرة أخرى.',
            RATE_LIMITED: 'طلبات كثيرة جدًا. يرجى الانتظار قليلاً والمحاولة مرة أخرى.',
        },
        tour: {
            searchBox: {
                title: 'تعرّف على البحث الذكي ✨',
                description: 'فقط أخبرنا بما تريد — مثل "أرخص رحلة صباحية مع أمتعة وترتيب حسب السعر" — وسنجد لك أفضل الرحلات.',
            },
            sortMessage: {
                title: 'نتائج مرتبة لك',
                description: 'يتم ترتيب الرحلات بناءً على تفضيلاتك — مع موازنة السعر، المدة، عدد التوقفات، والتوقيت — لإظهار أفضل الخيارات أولاً.',
            },
            cardMetrics: {
                title: 'لماذا هذه الرحلة؟',
                description: 'اعرف سبب ترشيح الرحلة — جودة العرض، السعر مقارنة بالمتوسط، والمدة مقارنة بالمتوسط — في مكان واحد.',
            },
        },
        smartPreferenceOnlyPartialMatch: "إليك بعض الخيارات الرائعة القريبة من تفضيلاتك، حتى لو لم يكن التوقيت مطابقًا تمامًا — ألقِ نظرة.",
        smartPreferencePartialMatch: "الرحلات لا تطابق وقتك المفضل، لكنها تتوافق مع بعض تفضيلاتك الأخرى — ألقِ نظرة.",
        autoApplyPreferencesToggle: 'تطبيق التفضيلات المحفوظة للنتائج المحدثة',
    }




}
